<template>
  <div
    data-test-id="review-card"
    :class="
      twMerge(
        'bg-white p-2 w-[100%] lg:min-w-[290px] border border-stone-200 rounded-xl',
        $attrs.class
      )
    "
  >
    <div class="leading-5 text-sm text-stone-700 flex flex-col h-full">
      <slot name="header">
        <div class="flex justify-between">
          <slot name="title"></slot>
          <ReviewVerifiedBadge v-if="verified" />
          <Badge
            v-if="badge"
            class="px-1.5 h-auto py-[2px] rounded-md"
            :class="badge.classes"
            data-test-id="review-badge"
          >
            {{ badge.label }}
          </Badge>
        </div>
      </slot>

      <slot v-if="enableStars" name="stars">
        <div class="flex justify-between mt-2">
          <ReviewRatingStars :average-rating="averageRating" />
          <Chip v-if="isTeamevent">Teamevent</Chip>
        </div>
      </slot>

      <slot name="item-title">
        <div class="text-sm text-stone-700 font-normal my-2">
          {{ itemTitle }}
        </div>
      </slot>

      <div class="m-0 cursor-default select-none h-full">
        <ReadMore
          class="text-sm"
          data-test-id="review-content"
          :max-chars="230"
          :content="reviewContent"
          :prevent-expand="openReplyInSidebar"
          @toggle="onReviewContentToggle"
        />
      </div>

      <div
        v-if="(enableImages && computedImages.length > 0) || $slots.footer"
        class="mt-4"
      >
        <slot v-if="enableImages && computedImages.length > 0" name="images">
          <div
            class="flex gap-2 w-full flex-wrap overflow-hidden h-[80px] mb-4"
          >
            <NuxtLazyImg
              v-for="image in computedImages"
              :key="image.id ?? image.uuid"
              v-bind="getImageThumbAttrs(image)"
              class="h-20 w-20 rounded-xl object-cover cursor-pointer"
              alt=""
              @click="emit('click:image')"
            />
          </div>
        </slot>

        <slot v-if="$slots.footer" name="footer" v-bind="{ reviewedAt }" />
      </div>

      <ReviewPartnerReply
        v-if="supplierReply"
        class="mt-4"
        :reply="supplierReply"
        :open-sidebar-on-read-more="openReplyInSidebar"
        :show-divider="false"
        @expand="onPartnerReplyExpand"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { twMerge } from 'tailwind-merge'
import ReviewVerifiedBadge from './ReviewVerifiedBadge.vue'
import ReviewPartnerReply from './ReviewPartnerReply.vue'
import type { MediaItem, OrderItemVariety } from '~/composables/types/event'
import { toReviewMediaUrl } from '~/utils/mediaHelpers'

const REVIEW_THUMB_WIDTH = 360

const {
  averageRating = 0,
  reviewContent = '',
  reviewedAt = '',
  item_type,
  isTeamevent = false,
  enableImages = true,
  enableStars = true,
  verified = false,
  images = [],
  openReplyInSidebar = false,
  id,
} = defineProps<{
  averageRating: number
  reviewContent: string
  reviewerName: string
  reviewedAt: string
  isTeamevent: boolean
  enableImages?: boolean
  enableStars: boolean
  itemTitle?: string
  item_type?: OrderItemVariety
  verified: boolean
  images: MediaItem[]
  supplierReply?: string
  openReplyInSidebar?: boolean
  id?: string
}>()

const emit = defineEmits<{
  (e: 'click:image'): void
  (e: 'expand:partner-reply' | 'expand:review-content', id?: string): void
}>()

const { t } = useI18n()

const isProduction = useRuntimeConfig().public.appEnv === 'production'

const onReviewContentToggle = () => {
  if (openReplyInSidebar) {
    emit('expand:review-content', id)
  }
}

const onPartnerReplyExpand = () => {
  if (openReplyInSidebar) {
    emit('expand:partner-reply', id)
  }
}

const computedImages = computed(() => {
  return [...images].splice(0, 4)
})

const isVideoThumb = (image: MediaItem) =>
  image.url?.includes('.mp4') || image.url?.includes('.mov')

const getImageSrc = (image: MediaItem) => {
  if (isVideoThumb(image)) {
    return '/_nuxt3-static/reviews/icon-play.png'
  }

  return (
    toReviewMediaUrl(image, REVIEW_THUMB_WIDTH, { isProduction }) ||
    image.thumb ||
    image.cdn_url ||
    image.url
  )
}

const getImageThumbAttrs = (image: MediaItem) => {
  const src = getImageSrc(image)

  if (isVideoThumb(image)) {
    return { src }
  }

  return {
    src,
    provider: 'konfetti',
    densities: '1',
    sizes: '80px',
    aspectRatio: 'auto',
  }
}

const badge = computed(() => {
  if (!item_type) return null
  const map = {
    GROUP_EVENT: {
      classes: 'text-lime-900 bg-lime-50 border border-lime-900',
      label: t(`product.itemVariety.${item_type}`),
    },
    TEAM_EVENT: {
      classes: 'text-teal-700 bg-teal-50 border border-teal-700',
      label: t(`product.itemVariety.${item_type}`),
    },
    PUBLIC_EVENT: null,
  }

  return map[item_type]
})
</script>
